import type { GeoJsonFeatureCollection } from '@gpx-spa/geo-domain'
import { getMapStyleFromFeatureProperties } from '@gpx-spa/geo-domain'

type GeoFeature = GeoJsonFeatureCollection['features'][number]

/** Split GeoJSON features into glify-capable vs legacy Leaflet Path. */
export function partitionGlifyGeoJsonFeatures(
  features: GeoJsonFeatureCollection['features'],
): {
  glifyLines: GeoJsonFeatureCollection['features']
  glifyPolygons: GeoJsonFeatureCollection['features']
  legacy: GeoJsonFeatureCollection['features']
} {
  const glifyLines: GeoJsonFeatureCollection['features'] = []
  const glifyPolygons: GeoJsonFeatureCollection['features'] = []
  const legacy: GeoJsonFeatureCollection['features'] = []

  for (const f of features) {
    const t = f.geometry?.type
    if (t === 'LineString' || t === 'MultiLineString') {
      const s = getMapStyleFromFeatureProperties(
        f.properties as Record<string, unknown> | undefined,
      )
      const dashed = Array.isArray(s.lineDashArray) && s.lineDashArray.length > 0
      if (dashed || s.lineAntPath || s.lineGeodesic) {
        legacy.push(f)
      } else {
        glifyLines.push(f)
      }
      continue
    }
    if (t === 'Polygon' || t === 'MultiPolygon') {
      glifyPolygons.push(f)
      continue
    }
    legacy.push(f)
  }

  return { glifyLines, glifyPolygons, legacy }
}

/**
 * glify joins stack alpha, so a polygon outline reads brighter than the same
 * stroke on canvas. Width is scaled for every glify line in
 * `leafletLineWidthToGlifyWeight`.
 */
const POLYGON_STROKE_OPACITY_SCALE = 0.8

/**
 * Exterior (+ hole) rings as LineStrings for glify.lines stroke.
 * leaflet.glify `shapes({ border: true })` enables DEPTH_TEST without clearing depth,
 * which drops triangle fills and leaves only the border — so we stroke via lines instead.
 */
export function polygonStrokeLineFeatures(
  polygons: GeoJsonFeatureCollection['features'],
): GeoJsonFeatureCollection['features'] {
  const out: GeoJsonFeatureCollection['features'] = []
  for (const f of polygons) {
    const s = getMapStyleFromFeatureProperties(
      f.properties as Record<string, unknown> | undefined,
    )
    const lineProps = {
      ...(f.properties as Record<string, unknown> | null),
      map: {
        lineColor: s.stroke ?? s.fill ?? '#3388ff',
        lineWidth: s.strokeWidth ?? 2,
        lineOpacity: (s.strokeOpacity ?? 0.95) * POLYGON_STROKE_OPACITY_SCALE,
        ...(s.popup ? { popup: s.popup } : {}),
        ...(s.popupHtml ? { popupHtml: true as const } : {}),
      },
    }
    const geom = f.geometry
    if (!geom) continue
    if (geom.type === 'Polygon') {
      const rings = geom.coordinates as number[][][]
      for (const ring of rings) {
        if (!Array.isArray(ring) || ring.length < 2) continue
        out.push({
          type: 'Feature',
          properties: lineProps,
          geometry: { type: 'LineString', coordinates: ring },
        } as GeoFeature)
      }
    } else if (geom.type === 'MultiPolygon') {
      const polys = geom.coordinates as number[][][][]
      for (const poly of polys) {
        for (const ring of poly) {
          if (!Array.isArray(ring) || ring.length < 2) continue
          out.push({
            type: 'Feature',
            properties: lineProps,
            geometry: { type: 'LineString', coordinates: ring },
          } as GeoFeature)
        }
      }
    }
  }
  return out
}
